import { useMemo, useState } from "react";
import { useCustom, useGetLocale, useTranslate } from "@refinedev/core";
import {
  Button,
  SelectField,
  StatusBadge,
  type Column,
  type StatusTone,
} from "@fawkes/ui";

import { formatDate, formatEuro } from "../i18n/format";
import { IndeelbareTabel } from "../components/IndeelbareTabel";

// Crematorium (Epic 21, MR-2): the Crematies dossier list — the React port of the Jmix
// `CrematiedossierListView`. Columns: Dossier / Dier / Eigenaar / Status (badge) / Verwerkingsvorm /
// Betaling (badge) / Aangemeld / Totaal, newest first. Client-side search (dossiernummer + eigenaar)
// + status / verwerkingsvorm / betaalstatus filters. Row → opens the read-only detail. The Jmix view
// stays reachable by its @Route (strangler-fig).
//
// GET uses a RELATIVE url (the dataProvider prepends /api once). ROLE_CREMATORIUM_VIEW is
// server-enforced; a 403 surfaces inline.

interface DossierRow {
  id: string;
  dossierNummer: string | null;
  dierNaam: string | null;
  eigenaarNaam: string | null;
  relatieId: number | null;
  status: string;
  verwerkingsvorm: string | null;
  betaalStatus: string | null;
  aanmeldDatum: string | null;
  totaalBedrag: number | null;
}

const DOSSIER_STATUSSEN = [
  "CONCEPT",
  "AANGEMELD",
  "OPHALING_GEPLAND",
  "OPGEHAALD",
  "ONTVANGEN",
  "WACHT_OP_BETALING",
  "KLAAR_VOOR_CREMATIE",
  "GEPLAND",
  "GECREMEERD",
  "AS_URN_KLAAR_VOOR_RETOUR",
  "AFGEROND",
  "GEANNULEERD",
] as const;

const VORMEN = [
  "INDIVIDUEEL",
  "UITGEBREID",
  "COLLECTIEF",
  "RESOMEREN_INDIVIDUEEL",
  "RESOMEREN_COLLECTIEF",
  "BEGRAFENIS_INDIVIDUEEL",
  "BEGRAFENIS_ANONIEM",
  "DESTRUCTIE",
] as const;

const BETAAL_STATUSSEN = [
  "NOG_NIET_GEFACTUREERD",
  "GEFACTUREERD",
  "BETAALLINK_VERSTUURD",
  "DEELS_BETAALD",
  "BETAALD",
  "VERVALLEN",
  "HERINNERING_VERSTUURD",
  "GECREDITEERD",
] as const;

const STATUS_TONE: Record<string, StatusTone> = {
  AFGEROND: "success",
  GECREMEERD: "success",
  GEANNULEERD: "danger",
  WACHT_OP_BETALING: "warning",
};
const statusTone = (s: string): StatusTone => STATUS_TONE[s] ?? "neutral";

const BETAAL_TONE: Record<string, StatusTone> = {
  BETAALD: "success",
  DEELS_BETAALD: "warning",
  HERINNERING_VERSTUURD: "warning",
  VERVALLEN: "danger",
};
const betaalTone = (s: string): StatusTone => BETAAL_TONE[s] ?? "neutral";

export function CrematiesList({ onOpen }: { onOpen?: (id: string, relatieId?: number | null) => void } = {}) {
  const t = useTranslate();
  const locale = useGetLocale()() ?? "nl";

  const { data, isLoading, isError } = useCustom<DossierRow[]>({
    url: "crematorium/dossiers",
    method: "get",
  });
  const rows = data?.data ?? [];

  const [zoek, setZoek] = useState("");
  const [status, setStatus] = useState("");
  const [vorm, setVorm] = useState("");
  const [betaal, setBetaal] = useState("");

  const filtered = useMemo(() => {
    const q = zoek.trim().toLowerCase();
    return rows.filter((r) => {
      if (status && r.status !== status) return false;
      if (vorm && r.verwerkingsvorm !== vorm) return false;
      if (betaal && r.betaalStatus !== betaal) return false;
      if (!q) return true;
      return (
        (r.dossierNummer ?? "").toLowerCase().includes(q) ||
        (r.eigenaarNaam ?? "").toLowerCase().includes(q) ||
        (r.dierNaam ?? "").toLowerCase().includes(q)
      );
    });
  }, [rows, zoek, status, vorm, betaal]);

  const columns: Column<DossierRow>[] = [
    { header: t("crematies.col.dossier"), cell: (r) => r.dossierNummer ?? "—" },
    { header: t("crematies.col.dier"), cell: (r) => r.dierNaam ?? "—" },
    { header: t("crematies.col.eigenaar"), cell: (r) => r.eigenaarNaam ?? "—" },
    {
      header: t("crematies.col.status"),
      cell: (r) => (
        <StatusBadge tone={statusTone(r.status)}>{t(`crematies.status.${r.status}`)}</StatusBadge>
      ),
    },
    {
      header: t("crematies.col.verwerkingsvorm"),
      cell: (r) => (r.verwerkingsvorm ? t(`crematoriumTarief.vorm.${r.verwerkingsvorm}`) : "—"),
    },
    {
      header: t("crematies.col.betaling"),
      cell: (r) =>
        r.betaalStatus ? (
          <StatusBadge tone={betaalTone(r.betaalStatus)}>
            {t(`crematies.betaal.${r.betaalStatus}`)}
          </StatusBadge>
        ) : (
          "—"
        ),
    },
    {
      header: t("crematies.col.aangemeld"),
      cell: (r) => (r.aanmeldDatum ? formatDate(r.aanmeldDatum, locale) : "—"),
    },
    {
      header: t("crematies.col.totaal"),
      align: "right",
      cell: (r) => (r.totaalBedrag != null ? formatEuro(r.totaalBedrag, locale) : "—"),
    },
  ];

  if (onOpen) {
    columns.push({
      header: "",
      cell: (r) => (
        <Button variant="secondary" onClick={() => onOpen(r.id, r.relatieId)}>
          {t("common.open")}
        </Button>
      ),
    });
  }

  return (
    <div className="product-overview">
      <div className="asiel-tarief__panel">
        <div className="asiel-tarief__toolbar">
          <input
            type="search"
            autoComplete="off"
            className="fawkes-products-search__input"
            placeholder={t("crematies.search.placeholder")}
            value={zoek}
            onChange={(e) => setZoek(e.target.value)}
            aria-label={t("crematies.search.placeholder")}
          />
          <SelectField
            label={t("crematies.col.status")}
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            options={[
              { value: "", label: t("crematies.filter.alle") },
              ...DOSSIER_STATUSSEN.map((s) => ({ value: s, label: t(`crematies.status.${s}`) })),
            ]}
          />
          <SelectField
            label={t("crematies.col.verwerkingsvorm")}
            value={vorm}
            onChange={(e) => setVorm(e.target.value)}
            options={[
              { value: "", label: t("crematies.filter.alle") },
              ...VORMEN.map((v) => ({ value: v, label: t(`crematoriumTarief.vorm.${v}`) })),
            ]}
          />
          <SelectField
            label={t("crematies.col.betaling")}
            value={betaal}
            onChange={(e) => setBetaal(e.target.value)}
            options={[
              { value: "", label: t("crematies.filter.alle") },
              ...BETAAL_STATUSSEN.map((s) => ({
                value: s,
                label: t(`crematies.betaal.${s}`),
              })),
            ]}
          />
        </div>

        {isError && (
          <div className="asiel-tarief__notice asiel-tarief__notice--error">
            {t("crematies.load.error")}
          </div>
        )}

        <IndeelbareTabel<DossierRow> tabelKey="crematies-list"
          columns={columns}
          rows={filtered}
          getRowKey={(r) => r.id}
          empty={isLoading ? t("common.loading") : t("crematies.empty")}
        />
      </div>
    </div>
  );
}
